/**
 * The add-ons in the main navigation (ADR-025 §4.6, `AdminExtension::menu`): the menu entries a
 * running add-on declares are shown under "Tiện ích" in the sidebar, each to staff holding the
 * permission the add-on gives it, and go when the add-on is switched off. admin-api's
 * `GET /addon-menu` lists only those (subsystems/platform/facade/adapters/admin-api/src/admin/addons.rs); the sidebar keeps the
 * same rule and reads the list again when an add-on is switched on or off here
 * ([`addonMenuChanged`]) and every minute.
 *
 * Plain data and functions, so node tests can load it. Labels: `ux:sidebar.groups.addons`; an
 * entry's own label is the add-on's (both languages).
 */

import type { AddonMenuItem } from '../api/areas/addons';
import type { BilingualText } from '../api/areas/modules';
import { hubUrl } from './hubs.ts';

/** The permissions that grant each add-on permission (`Principal::staff_holds`): `addon.manage` implies `addon.operate`, which implies `addon.read`. */
export const ADDON_PERMISSION_HOLDERS: Readonly<Record<string, readonly string[]>> = {
  'addon.read': ['addon.read', 'addon.operate', 'addon.manage'],
  'addon.operate': ['addon.operate', 'addon.manage'],
  'addon.manage': ['addon.manage'],
};

/** Every add-on permission: who holds none has no add-on entry to see. */
export const ADDON_PERMISSIONS: readonly string[] = ADDON_PERMISSION_HOLDERS['addon.read'] ?? [];

/** One entry of the "Tiện ích" group. */
export interface AddonNavEntry {
  /** `<addon>/<entry key>`. */
  id: string;
  addon: string;
  page: string;
  label: BilingualText;
  /** The add-on's name (a tooltip). */
  addonName: BilingualText;
  /** The permission the add-on gives the entry. */
  permission: string;
  url: string;
}

/** Where an add-on's page opens: Cài đặt › Module › Tiện ích, the add-on, that page. */
export function addonPageUrl(addon: string, page: string): string {
  return hubUrl('settings', 'modules', undefined, { branch: 'addons', addon, page });
}

/**
 * The entries to show from `GET /addon-menu`: those whose permission `has` grants (an add-on
 * permission is held through the ones that imply it; a permission outside the add-ons' closed set
 * never is), once each, in the order given.
 */
export function addonNavEntries(items: readonly AddonMenuItem[], has: (perms: readonly string[]) => boolean): AddonNavEntry[] {
  const seen = new Set<string>();
  const out: AddonNavEntry[] = [];
  for (const i of items) {
    const holders = ADDON_PERMISSION_HOLDERS[i.permission];
    const id = `${i.addon}/${i.key}`;
    if (!holders || !has(holders) || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, addon: i.addon, page: i.page, label: i.label, addonName: i.addon_name, permission: i.permission, url: addonPageUrl(i.addon, i.page) });
  }
  return out;
}

/** Whether a location shows the entry's page (`/settings?tab=modules&branch=addons&addon=…&page=…`). */
export function isAddonEntryOpen(entry: AddonNavEntry, pathname: string, search: string): boolean {
  if (pathname.replace(/\/+$/, '') !== '/settings') return false;
  const p = new URLSearchParams(search);
  return p.get('tab') === 'modules' && p.get('branch') === 'addons' && p.get('addon') === entry.addon && p.get('page') === entry.page;
}

const listeners = new Set<() => void>();

/** Calls `listener` when an add-on is switched on or off here; returns how to stop. */
export function onAddonMenuChanged(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** An add-on's state changed (Cài đặt › Module › Tiện ích): the navigation reads its entries again. */
export function addonMenuChanged(): void {
  for (const l of listeners) l();
}
